import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { URLS, ZITI_URLS } from '@netfoundry-ui/shared/services';
import { CoverageSankeyDialogComponent, CoverageDialogData } from '../services-from-traffic/coverage-sankey-dialog.component';
import { CoverageServiceInput, isSegmentCovered } from '../services-from-traffic/coverage-sankey.adapter';
import {
    collapsePortRanges,
    ServiceTemplate,
    ServiceTemplateService,
    suggestServiceName,
} from '@netfoundry-ui/shared/service-templates';
import { Subscription } from 'rxjs';

type ServiceMethod = 'simple' | 'template';
/** How each side is reached/hosted: a real address, or an embedded SDK application. */
type SideMode = 'address' | 'sdk';

/** One service in the recommended-services preview: its suggested name and display labels. */
interface PreviewService {
    name: string;
    destLabel: string;
    /** The destination as address:port when there is a single port, else just the address. */
    destEndpoint: string;
    portsLabel: string;
    multiPort: boolean;
}

/**
 * "Create services from traffic" chooser, routed at `services/select` (beta) only when a traffic
 * selection is present - reached from the Traffic Analysis page or Microsegments, which pass the
 * selection descriptor (every observed destination address and port, the source and host identities,
 * and the time window) as query params. It reviews that selection and offers Recommended (services
 * seeded from it) or Custom (a blank build page, or From template). The standard services-list "+"
 * carries no selection, so it lands on the 3-card ServiceCardListComponent instead.
 */
@Component({
    selector: 'app-service-type-chooser',
    templateUrl: './service-type-chooser.component.html',
    styleUrls: ['./service-type-chooser.component.scss'],
    standalone: false,
})
export class ServiceTypeChooserComponent implements OnInit, OnDestroy {
    /** The method the user has selected; restored from the URL (set when stepping back), else Simple. */
    selected: ServiceMethod;

    // --- Simple "service shape" (decided here, carried to the Simple form) ---
    /** How clients reach the service: a real address, or an embedded SDK application. */
    accessMode: SideMode = 'address';
    /** How the service is hosted: a server address, or an SDK hosting application. */
    hostMode: SideMode = 'address';

    /** Which setup the operator chose: 'recommended' pre-configures services from the selection; 'custom'
     *  opens the build page blank for a hand-built service. Carried to the build page. */
    setup: 'recommended' | 'custom' = 'recommended';

    // --- From template ---
    /** The template id picked from the card grid; carried to the template form to pre-select it. */
    selectedTemplateId = '';
    templates: ServiceTemplate[] = [];
    templatesLoading = true;
    /** Typeahead text and how many cards to show before "Show more", matching the template workflow. */
    templateSearch = '';
    readonly templateShowMoreStep = 4;
    templateVisibleCount = this.initialTemplateCount();

    /** Whether the read-only calendar popover (opened from the observed-window pill) is showing. */
    showCalendar = false;

    private sub = new Subscription();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private templateService: ServiceTemplateService,
        private dialog: MatDialog
    ) {
        const p = this.route.snapshot.queryParamMap;
        this.selected = p.get('method') === 'template' ? 'template' : 'simple';
        // Restore the shape and template pick when stepping back from a create screen.
        if (p.get('accessMode') === 'sdk') {
            this.accessMode = 'sdk';
        }
        if (p.get('hostMode') === 'sdk') {
            this.hostMode = 'sdk';
        }
        this.selectedTemplateId = p.get('templateId') || '';
    }

    ngOnInit(): void {
        this.sub.add(this.templateService.templates$.subscribe((list) => (this.templates = list)));
        this.templateService
            .loadTemplates()
            .catch(() => undefined)
            .finally(() => (this.templatesLoading = false));
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    get sourceLabel(): string {
        return this.route.snapshot.queryParamMap.get('label') || '';
    }

    get sourceIdentities(): string[] {
        return this.splitParam(this.route.snapshot.queryParamMap.get('srcIdentity'));
    }

    get sourceIps(): string[] {
        // "(empty)" is the fabric-circuits placeholder for identity initiators with no source IP.
        return this.splitParam(this.route.snapshot.queryParamMap.get('srcIp')).filter((ip) => ip !== '(empty)');
    }

    /** True when we have the broken-out source fields; otherwise fall back to the combined label. */
    get hasSourceDetail(): boolean {
        return this.sourceIdentities.length > 0 || this.sourceIps.length > 0;
    }

    get destIps(): string[] {
        return this.splitParam(this.route.snapshot.queryParamMap.get('destIp'));
    }

    get destPorts(): string[] {
        return this.splitParam(this.route.snapshot.queryParamMap.get('destPort'));
    }

    /** The recommended services this selection would create: one per destination address, grouping its
     *  ports. Mirrors the build page's recommended seeding so the preview is true. */
    get recommendedServices(): PreviewService[] {
        const byAddress = this.portsByDestAddress();
        const addresses = Array.from(byAddress.keys());
        return addresses.map((address) => this.previewService([address], byAddress.get(address) ?? []));
    }

    /** Past a handful of services, the card list gets long; switch to a compact table. */
    get useTablePreview(): boolean {
        return this.recommendedServices.length > 5;
    }

    private previewService(addresses: string[], ports: string[]): PreviewService {
        const portNums = ports.map((p) => Number(p)).filter((n) => Number.isFinite(n));
        const name = suggestServiceName(addresses, collapsePortRanges(portNums));
        const multi = addresses.length > 1;
        const destLabel = multi ? `${addresses.length} destinations` : addresses[0] || '';
        const destEndpoint = !multi && ports.length === 1 ? `${addresses[0]}:${ports[0]}` : destLabel;
        return { name, destLabel, destEndpoint, portsLabel: ports.join(', '), multiPort: ports.length > 1 };
    }

    private observedPairs(): { address: string; port: string }[] {
        return this.splitParam(this.route.snapshot.queryParamMap.get('destPairs')).map((pair) => {
            const idx = pair.lastIndexOf(':');
            return { address: idx > 0 ? pair.slice(0, idx) : pair, port: idx > 0 ? pair.slice(idx + 1) : '' };
        });
    }

    private portsByDestAddress(): Map<string, string[]> {
        const map = new Map<string, string[]>();
        for (const { address, port } of this.observedPairs()) {
            if (!address) {
                continue;
            }
            const ports = map.get(address) ?? [];
            if (port && !ports.includes(port)) {
                ports.push(port);
            }
            map.set(address, ports);
        }
        return map;
    }

    private distinctPorts(byAddress: Map<string, string[]>): string[] {
        const set = new Set<string>();
        byAddress.forEach((ports) => ports.forEach((p) => set.add(p)));
        return Array.from(set);
    }

    /** The recommended services as coverage inputs: each matches its destination endpoints and admits any
     *  source (empty allowedSourceAddresses), so the preview shows the selection fully covered - these
     *  services are built from it. */
    private coverageServiceInputs(): CoverageServiceInput[] {
        const byAddress = this.portsByDestAddress();
        const addresses = Array.from(byAddress.keys());
        if (!addresses.length) {
            return [];
        }
        const build = (addrs: string[], ports: string[]): CoverageServiceInput => {
            const portNums = ports.map((p) => Number(p)).filter((n) => Number.isFinite(n));
            return {
                name: suggestServiceName(addrs, collapsePortRanges(portNums)),
                accessMode: 'address',
                addresses: [...addrs],
                ports: [...ports],
                allowedSourceAddresses: [],
                dialAttributes: [],
            };
        };
        return addresses.map((address) => build([address], byAddress.get(address) ?? []));
    }

    /** Open the selected flows & coverage in the same Sankey modal the build page uses. */
    openFlowPreview(): void {
        const pairs = this.observedPairs();
        if (!pairs.length) {
            return;
        }
        const services = this.coverageServiceInputs();
        const p = this.route.snapshot.queryParamMap;
        const data: CoverageDialogData = {
            services,
            pairs,
            destIp: p.get('destIp') || '',
            destPort: p.get('destPort') || '',
            srcIdentities: this.sourceIdentities,
            srcIps: this.sourceIps,
            hostIdentities: this.hostIdentities,
            start: Number(p.get('start')) || undefined,
            end: Number(p.get('end')) || undefined,
            label: this.sourceLabel || undefined,
            coveredCount: pairs.filter((pair) => isSegmentCovered(pair.address, pair.port, services)).length,
            totalSegments: pairs.length,
        };
        this.dialog.open(CoverageSankeyDialogComponent, {
            data,
            panelClass: 'cov-dialog-panel',
            width: '96vw',
            maxWidth: '1500px',
            autoFocus: false,
        });
    }

    /** The identities observed serving the destination ("who hosts it"), shown on the destination side. */
    get hostIdentities(): string[] {
        return this.splitParam(this.route.snapshot.queryParamMap.get('hostIdentity'));
    }

    /** Human-readable window the traffic was observed over, e.g. "Oct 13 - Oct 20, 2026". */
    get observedRange(): string {
        const start = Number(this.route.snapshot.queryParamMap.get('start'));
        const end = Number(this.route.snapshot.queryParamMap.get('end'));
        if (!start || !end) {
            return '';
        }
        const day = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
        const full = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${day.format(new Date(start))} - ${full.format(new Date(end))}`;
    }

    /** Observed-window bounds (epoch ms) that seed the read-only calendar popover. */
    get observedStartMs(): number | null {
        return Number(this.route.snapshot.queryParamMap.get('start')) || null;
    }
    get observedEndMs(): number | null {
        return Number(this.route.snapshot.queryParamMap.get('end')) || null;
    }

    toggleCalendar(): void {
        this.showCalendar = !this.showCalendar;
    }

    select(method: ServiceMethod): void {
        this.selected = method;
    }

    get hasTemplates(): boolean {
        return this.templates.length > 0;
    }

    /** Templates matching the search box (name or description). */
    get filteredTemplates(): ServiceTemplate[] {
        const term = this.templateSearch.trim().toLowerCase();
        if (!term) {
            return this.templates;
        }
        return this.templates.filter((t) => `${t.name} ${t.description ?? ''}`.toLowerCase().includes(term));
    }

    /** The cards shown now: the first page, always keeping a picked card visible even if it sorts past it. */
    get visibleTemplates(): ServiceTemplate[] {
        const filtered = this.filteredTemplates;
        const selectedIndex = filtered.findIndex((t) => t.id === this.selectedTemplateId);
        return filtered.slice(0, Math.max(this.templateVisibleCount, selectedIndex + 1));
    }

    get hiddenTemplateCount(): number {
        return this.filteredTemplates.length - this.visibleTemplates.length;
    }

    showMoreTemplates(): void {
        this.templateVisibleCount = this.visibleTemplates.length + this.templateShowMoreStep;
    }

    onTemplateSearch(value: string): void {
        this.templateSearch = value;
        this.templateVisibleCount = this.initialTemplateCount();
    }

    selectTemplate(id: string): void {
        this.selectedTemplateId = id;
    }

    /** Fewer cards on a narrow panel so the grid stays two readable columns. */
    private initialTemplateCount(): number {
        const width = typeof window === 'undefined' ? 1280 : window.innerWidth;
        return width < 1100 ? 4 : 6;
    }

    /** Continue is blocked only when From template is chosen but no template is picked yet. */
    get canContinue(): boolean {
        // Recommended is always ready; a Custom template choice needs a template picked first.
        if (this.setup === 'recommended') {
            return true;
        }
        return this.selected !== 'template' || !!this.selectedTemplateId;
    }

    goToTemplates(): void {
        this.router.navigateByUrl(ZITI_URLS.ZITI_SERVICE_TEMPLATES);
    }

    continue(): void {
        if (!this.canContinue) {
            return;
        }
        // From template (only offered under Custom) opens the template form.
        if (this.selected === 'template') {
            this.router.navigate([URLS.SERVICE_TEMPLATE], { queryParams: this.templateParams() });
            return;
        }
        // Everything else opens the composable build page: Recommended seeds services from the selection,
        // Custom opens it blank.
        this.router.navigate([URLS.SERVICE_CREATE_FROM_TRAFFIC], { queryParams: this.fromTrafficParams() });
    }

    /** Params for the "Create services from traffic" page: the whole traffic selection plus the access/host
     *  shape chosen here, which the page uses as the first service card's defaults. */
    private fromTrafficParams(): Params {
        const params = this.trafficParams();
        params['accessMode'] = this.accessMode;
        params['hostMode'] = this.hostMode;
        // 'custom' opens the build page blank; 'recommended' seeds services from the selection.
        params['setup'] = this.setup;
        return params;
    }

    /** Params for the template create step: the picked template id, plus the traffic descriptor so the
     *  back link returns here with the selection (the template form ignores it for seeding). */
    private templateParams(): Params {
        const params = this.trafficParams();
        params['method'] = 'template';
        if (this.selectedTemplateId) {
            params['templateId'] = this.selectedTemplateId;
        }
        return params;
    }

    back(): void {
        // Tell Traffic Analysis to restore the filters, view and selection the user left on.
        this.router.navigate([URLS.TRAFFIC_ANALYSIS], { queryParams: { restore: 1 } });
    }

    private splitParam(value: string | null): string[] {
        return (value || '')
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean);
    }

    /** The traffic-selection descriptor to pass through, or {} when not seeded from Traffic Analysis. */
    private trafficParams(): Params {
        const p = this.route.snapshot.queryParamMap;
        const params: Params = {};
        ['destIp', 'destPort', 'destPairs', 'srcIdentity', 'srcIp', 'hostIdentity', 'start', 'end', 'label'].forEach(
            (key) => {
                const value = p.get(key);
                if (value) {
                    params[key] = value;
                }
            }
        );
        return params;
    }
}
